import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames";
import { Form, Formik } from "formik";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";

import { Button, Input, Typography } from "@/shared/ui";
import { InputProps } from "@/shared/ui/input/input.types";
import { useOnboardingStore } from "@/stores";
import CrossIcon from "@/svgs/cross.svg";
import InfoIcon from "@/svgs/info.svg";

import styles from "./photo.module.scss";
import { PhotoTabFormScheme } from "./photo.types";

const PhotoTab: FC = () => {
  const { push } = useRouter();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentStep = useOnboardingStore((state) => state.currentStep);
  const userImageFD = useOnboardingStore((state) => state.userImageFormData);
  const isPhotoVerified = useOnboardingStore((state) => state.isPhotoVerified);
  const { setCurrentStep, setUserImageFD, setIsPhotoVerified, addPassedStep } = useOnboardingStore(
    (state) => state.actions
  );

  const [isHintOpened, setIsHintOpened] = useState(false);
  const [previewURL, setPreviewURL] = useState<string>();

  useEffect(() => {
    if (userImageFD) {
      const file = userImageFD.get("profileImage") as File;

      if (file && file.type.startsWith("image/")) {
        const imageURL = URL.createObjectURL(file);
        setPreviewURL(imageURL);

        return () => URL.revokeObjectURL(imageURL);
      }
    }
  }, [userImageFD]);

  const onChangeInputHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      const profileImageFormData = new FormData();
      profileImageFormData.append("profileImage", event.target.files[0]);

      setUserImageFD(profileImageFormData);
    }
  };
  const onButtonClickHandler = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const onSubmitHandler = () => {
    if (!isPhotoVerified) {
      return push("/auth/onboarding/verify-photo");
    }

    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };
  const onResetData = () => {
    setUserImageFD(undefined);
    setIsPhotoVerified(undefined);
  };

  const inputMeta = useMemo(() => {
    let variant: InputProps["variant"];
    let message: InputProps["message"];
    const value = userImageFD && (userImageFD.get("profileImage") as File).name;

    if (!userImageFD) {
      return {
        variant,
        message,
        value: "",
      };
    }

    if (isPhotoVerified === true) {
      variant = "success";
      message = "Verified";
    } else if (isPhotoVerified === false) {
      variant = "error";
      message = "Not verified. Try again";
    }
    return { variant, message, value };
  }, [userImageFD, isPhotoVerified]);

  return (
    <main className={styles.photoTab}>
      <div className={styles.title}>
        <Typography semi variant="title-2">
          Submit Your Profile Picture
        </Typography>

        <Tooltip.Provider>
          <Tooltip.Root open={isHintOpened} onOpenChange={(v) => setIsHintOpened(v)}>
            <Tooltip.Trigger asChild>
              <button type="button" onClick={() => setIsHintOpened((prev) => !prev)}>
                <InfoIcon className={styles.infoIcon} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content align="center" className={styles.tooltip} sideOffset={4}>
                Please follow these tips to ensure a successful upload:
                <ul>
                  <li>- Face fully visible, no covers</li>
                  <li>- Good lighting, no shadows</li>
                  <li>- Neutral expression, no filters, neutral background</li>
                  <li>- Clear, recent photo</li>
                  <li>- Only you in the picture</li>
                </ul>
                <Tooltip.Arrow className={styles.Arrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>

      <Formik<PhotoTabFormScheme>
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{ profileImage: { path: "", url: "", type: "image" } }}
        onSubmit={onSubmitHandler}
      >
        <Form className={styles.form}>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept="image/*"
            multiple={false}
            onChange={onChangeInputHandler}
          />
          <div className={styles.fileInput}>
            <Input
              readOnly
              placeholder="Profile Image"
              startContent={
                userImageFD && (
                  <CrossIcon
                    type="button"
                    role="button"
                    tabIndex={0}
                    className={classNames(styles.crossIcon, isPhotoVerified === false && styles.error)}
                    onClick={onResetData}
                  />
                )
              }
              value={inputMeta.value}
              variant={inputMeta.variant}
              message={inputMeta.message}
              className={styles.innerInput}
            />
            <Button variant="outlined" type="button" className={styles.button} onClick={onButtonClickHandler}>
              Choose file
            </Button>
          </div>

          {previewURL && (
            <div className={styles.preview}>
              <Image priority fill src={previewURL} alt="uploaded-photo" />
            </div>
          )}

          <Button fullWidth size="big" type="button" style={{ zIndex: 1 }} onClick={onSubmitHandler}>
            {isPhotoVerified ? "Continue" : "Verify"}
          </Button>
        </Form>
      </Formik>
    </main>
  );
};

export { PhotoTab };
